import React from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import DesktopSearch from 'home/components/SearchHero/DesktopSearch'
import MobileSearch from 'home/components/SearchHero/MobileSearch'
import Pane from 'components/Common/Pane'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import ToggleButton from 'LuxKit/ToggleButton'
import { useLocation } from 'react-router'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import HeroImage from 'content/components/HeroImage'

const Wrapper = styled.div`
  ${mediaQueryDown.mobile} {
    &.Navbar {
      padding-bottom: ${rem(46)};
    }
  }
`

const HeroContainer = styled(HeroImage)`
  ${mediaQueryDown.mobile} {
    padding: ${rem(20)} ${rem(8)};
  }

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(98)};
  }

  &.hero-container {
    padding: 0;
  }

  &.navbar-container {
    position: relative;
  }
`

const HeroContent = styled(LayoutContainer)`
  position: relative;
`

const HeroDimmedOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
  pointer-events: none;
`

const GlobalDesktopSearchContainer = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: unset;
  }

  ${mediaQueryUp.desktop} {
    position: relative;
    top: ${rem(80)};
  }
`

const NavbarContainer = styled.div`
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

const GlobalMobileSearchContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(12)};

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const Title = styled(Heading)`
  text-align: left;

  ${mediaQueryUp.desktop} {
    text-align: center;
  }
`

const Description = styled(BodyText)`
  text-align: left;

  ${mediaQueryUp.desktop} {
    text-align: center;
  }
`

const TabsCard = styled(Pane)`
  margin-top: ${rem(-32)};
  border-radius: ${rem(2)};
  padding: ${rem(12)} 0;
  position: relative;
  z-index: 1;
  border-radius: ${rem(2)};
`

const Tabs = styled(CardCarousel)`
  padding: 0 ${rem(12)};
`

const StyledBreakpoint = styled(CSSBreakpoint)`
  position: absolute;
  bottom: ${rem(-46)};
  width: 100%;
`

function SearchHero({
  image,
  title,
  subtitle,
  toggle,
  navBar,
}: App.ComponentHeroImage) {
  const hasContent = title && image.id
  const { pathname } = useLocation()
  return (
    <Wrapper className={clsx(toggle)}>
      {hasContent &&
        <HeroContainer
          alt={title}
          fit="center"
          gravity="auto"
          id={image.id!}
          aspectRatio="10:1"
          tabletAspectRatio="4:1"
          className="hero-container"
        >
          <HeroDimmedOverlay aria-hidden="true" role="presentation" />
          <HeroContent>
            <VerticalSpacer gap={20}>
              <Title colour="neutral-eight" variant="heading1" className="hero-heading">
                {title}
              </Title>
              {subtitle && <Description colour="neutral-eight" variant="large">{subtitle}</Description>}
              {toggle === 'Search Bar' && <>
                <GlobalMobileSearchContainer>
                  <MobileSearch />
                </GlobalMobileSearchContainer>
                <GlobalDesktopSearchContainer>
                  <DesktopSearch />
                </GlobalDesktopSearchContainer>
              </>}
            </VerticalSpacer>
          </HeroContent>
          {toggle === 'Navbar' && <StyledBreakpoint min="tablet">
            <LayoutContainer className="navbar-container" size="sm">
              <NavbarContainer>
                <TabsCard paletteType="default">
                  <Tabs pageSize={navBar.length < 5 ? navBar.length : 'auto'} gap={0}>
                    {navBar.map((item) => <ToggleButton
                      kind="primary"
                      orientation="vertical"
                      key={`${item.title} - ${item.to}`}
                      title={item.title}
                      subtitle={item.subtitle}
                      to={item.to}
                      isSelected={item.to === pathname.slice(3)}
                    />)}
                  </Tabs>
                </TabsCard>
              </NavbarContainer>
            </LayoutContainer>
          </StyledBreakpoint>}
          {toggle === 'Navbar' && <StyledBreakpoint max="mobile">
            <NavbarContainer>
              <TabsCard paletteType="default">
                <Tabs gap={0}>
                  {navBar.map((item) => <ToggleButton
                    kind="primary"
                    orientation="vertical"
                    key={`${item.title} - ${item.to}`}
                    title={item.title}
                    subtitle={item.subtitle}
                    to={item.to}
                    isSelected={item.to === pathname.slice(3)}
                  />)}
                </Tabs>
              </TabsCard>
            </NavbarContainer>
          </StyledBreakpoint>}
        </HeroContainer>}
    </Wrapper>
  )
}

export default SearchHero
